import { motion } from 'motion/react'
import bismillah from '#/assets/bismillah.svg'
import logoWhite from '#/assets/logo-icon-yellow.png'

interface PageLoaderProps {
  title?: string
  subtitle?: string
}

const EASE_SMOOTH = [0.22, 1, 0.36, 1] as const

export default function PageLoader({
  title = 'Loading Waallamah...',
  subtitle = 'Preparing your Islamic learning experience',
}: PageLoaderProps) {
  return (
    <div className="fixed inset-0 z-50 min-h-[100dvh] w-full flex flex-col items-center justify-center overflow-hidden bg-[#041a14] px-6 py-20 text-white">
      {/* ─── Ambient Glows & Background ──────────────────────────────── */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[550px] w-[550px] rounded-full bg-primary-500/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 right-10 h-[450px] w-[450px] rounded-full bg-secondary-500/20 blur-[120px]"
      />

      {/* ─── Center Morphing Showcase ─────────────────────────────────── */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-md">
        {/* Bismillah Calligraphy */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE_SMOOTH }}
          className="mb-8"
        >
          <img
            alt="Bismillah"
            src={bismillah}
            className="w-44 max-w-full drop-shadow-[0_4px_16px_rgba(255,182,6,0.3)] filter brightness-110"
          />
        </motion.div>

        {/* Morphing SVG Geometry + Glowing Center Logo */}
        <div className="relative flex items-center justify-center size-36 mb-8">
          {/* Ambient Glow behind SVG */}
          <div className="absolute inset-0 rounded-full bg-primary-500/20 blur-xl animate-pulse" />

          {/* Morphing Islamic Geometry SVG */}
          <svg
            className="absolute inset-0 size-full overflow-visible"
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer Rotating Octagram Star (Morphing path draw) */}
            <motion.polygon
              points="50,5 61,28 85,15 72,39 95,50 72,61 85,85 61,72 50,95 39,72 15,85 28,61 5,50 28,39 15,15 39,28"
              stroke="#ffb606"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="rgba(255, 182, 6, 0.04)"
              animate={{
                rotate: 360,
                scale: [0.96, 1.04, 0.96],
                strokeOpacity: [0.4, 0.9, 0.4],
              }}
              transition={{
                rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
                scale: { duration: 3.5, repeat: Infinity, ease: 'easeInOut' },
                strokeOpacity: {
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }}
              style={{ originX: '50px', originY: '50px' }}
            />

            {/* Middle Rotating Geometry (Counter-rotating) */}
            <motion.rect
              x="22"
              y="22"
              width="56"
              height="56"
              rx="12"
              stroke="#4fb8b2"
              strokeWidth="1"
              strokeDasharray="6 4"
              fill="none"
              animate={{
                rotate: -360,
                scale: [1.02, 0.94, 1.02],
              }}
              transition={{
                rotate: { duration: 16, repeat: Infinity, ease: 'linear' },
                scale: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
              }}
              style={{ originX: '50px', originY: '50px' }}
            />

            {/* Inner Sacred Geometry Circle with Drawn Path */}
            <motion.circle
              cx="50"
              cy="50"
              r="34"
              stroke="#ffb606"
              strokeWidth="0.8"
              strokeDasharray="4 6"
              fill="rgba(4, 26, 20, 0.6)"
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: 'linear',
              }}
              style={{ originX: '50px', originY: '50px' }}
            />
          </svg>

          {/* Center Logo with Breathing Scale & Light Shimmer */}
          <motion.div
            className="relative z-10 flex size-16 items-center justify-center rounded-2xl bg-gradient-to-b from-[#072a21] to-[#041913] border border-primary-500/40 p-2 shadow-[0_0_30px_rgba(255,182,6,0.35)]"
            animate={{
              scale: [0.95, 1.05, 0.95],
              boxShadow: [
                '0 0 20px rgba(255,182,6,0.25)',
                '0 0 40px rgba(255,182,6,0.5)',
                '0 0 20px rgba(255,182,6,0.25)',
              ],
            }}
            transition={{
              duration: 2.8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <img
              alt="Waallamah Emblem"
              src={logoWhite}
              className="size-full object-contain filter drop-shadow"
            />
          </motion.div>
        </div>

        {/* ─── Typography & Progress Micro-bar ────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: EASE_SMOOTH }}
          className="space-y-2.5"
        >
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white drop-shadow-sm">
            {title}
          </h2>

          <p className="text-xs sm:text-sm text-neutral-300/80 max-w-xs mx-auto leading-relaxed">
            {subtitle}
          </p>

          {/* Shimmering Linear Progress Track */}
          <div className="mx-auto mt-5 h-1 w-44 overflow-hidden rounded-full bg-white/10 p-0.5">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-primary-500 via-primary-300 to-primary-500 shadow-[0_0_10px_rgba(255,182,6,0.7)]"
              animate={{
                x: ['-100%', '100%'],
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  )
}
